import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { ApiService } from '@/services/api/ApiService';
import { TokenService } from '@/services/auth/TokenService';
import { UserService } from '@/services/users/UserService';
import { Profile } from '@/types/auth/auth';

interface CreateUserData {
  email: string;
  password?: string;
  full_name: string;
  role: string;
  avatar?: string;
  sendWelcomeEmail?: boolean;
}

interface ResetPasswordPayload {
  userId: string;
  password: string;
}

interface UpdateUserPayload {
  userId: string;
  profileUpdates?: Partial<Profile>;
  password?: string;
}

interface CreateSubscriptionPayload {
  userId: string;
  status: string;
  quantity?: number;
  extra_quantity?: number;
  stripe_id?: string | null;
  trial_ends_at?: string | null;
  ends_at?: string | null;
}

interface UpdateSubscriptionPayload {
  userId: string;
  subscriptionId: string | number;
  status: string;
  quantity?: number;
  extra_quantity?: number;
  stripe_id?: string | null;
  trial_ends_at?: string | null;
  ends_at?: string | null;
}

export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateUserData) => {
      if (data.password && data.password.length < 6) {
        throw new Error("Password must be at least 6 characters long");
      }

      const token = await TokenService.getAccessToken();
      if (!token) {
        throw new Error("Session expired. Please refresh the page and try again.");
      }

      const { data: profileData, error: profileError } = await supabase.functions.invoke("handle-profile", {
        body: {
          mode: "admin-create",
          email: data.email,
          password: data.password || undefined,
          role: data.role,
          full_name: data.full_name,
          avatar_url: data.avatar,
          sendWelcomeEmail: data.sendWelcomeEmail ?? false,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (profileError) throw profileError;
      if (!profileData.success) throw new Error(profileData.error || "Failed to create user");

      return profileData.profile;
    },
    onSuccess: () => {
      toast.success("User created successfully");
      queryClient.invalidateQueries({ queryKey: ["users-with-subscriptions"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create user");
    },
  });
};

export const lastWelcomeEmailQueryKey = (userId: string) => ["last-welcome-email", userId];

export const useLastWelcomeEmail = (userId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: lastWelcomeEmailQueryKey(userId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_logs")
        .select("created_at, status")
        .eq("user_id", userId)
        .eq("email_type", "welcome_user_created")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled,
  });
};

export const useSendWelcomeEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const token = await TokenService.getAccessToken();
      if (!token) {
        throw new Error("Session expired. Please refresh the page and try again.");
      }

      const { data, error } = await supabase.functions.invoke("handle-profile", {
        body: {
          mode: "send-welcome-email",
          userId,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || "Failed to send welcome email");

      return data;
    },
    onSuccess: (data, userId) => {
      toast.success(data.message || "Welcome email sent successfully");
      queryClient.invalidateQueries({ queryKey: lastWelcomeEmailQueryKey(userId) });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to send welcome email");
    },
  });
};

export const genieMailboxProvisioningStatusQueryKey = (userId: string) => ["genie-mailbox-provisioning-status", userId];

interface GenieMailboxProvisioningStatus {
  isProvisioning: boolean;
  total: number;
  provisioned: number;
}

export const useGenieMailboxProvisioningStatus = (userId: string, enabled: boolean = true) => {
  return useQuery<GenieMailboxProvisioningStatus>({
    queryKey: genieMailboxProvisioningStatusQueryKey(userId),
    queryFn: async () => {
      const { data: agents, error: agentsError } = await supabase.from("agents").select("id").eq("user_id", userId);
      if (agentsError) throw agentsError;
      if (!agents || agents.length === 0) return { isProvisioning: false, total: 0, provisioned: 0 };

      const { data: mailboxes, error: mailboxesError } = await supabase
        .from("agent_mailboxes")
        .select("status")
        .in("agent_id", agents.map((agent) => agent.id));
      if (mailboxesError) throw mailboxesError;

      const provisioned = (mailboxes || []).filter((m) => m.status === "provisioned").length;
      const isProvisioning = (mailboxes || []).some((m) => m.status === "pending");

      return { isProvisioning, total: agents.length, provisioned };
    },
    enabled,
    refetchInterval: (query) => (query.state.data?.isProvisioning ? 3000 : false),
  });
};

export const useProvisionGenieMailboxes = (options: { showToast?: boolean } = {}) => {
  const { showToast = true } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const token = await TokenService.getAccessToken();
      if (!token) {
        throw new Error("Session expired. Please refresh the page and try again.");
      }

      // Fire-and-forget: the edge function runs the bulk provisioning loop
      // to completion server-side and reports the outcome via a broadcast
      // on the "agent-creation" channel (event: mailbox_bulk_provisioned).
      void supabase.functions.invoke("handle-agent", {
        body: {
          action: "async-mailbox-provision-bulk",
          userId,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    },
    onSuccess: (_, userId) => {
      if (showToast) toast.info("Generating genie mailboxes — this may take a moment");
      queryClient.invalidateQueries({ queryKey: genieMailboxProvisioningStatusQueryKey(userId) });
    },
    onError: (error: Error) => {
      if (showToast) toast.error(error.message || "Failed to generate genie mailboxes");
    },
  });
};

interface GenieMailboxDeletionResult {
  agentId: string;
  agentName: string;
  status: "deleted" | "skipped" | "failed";
  error?: string;
}

interface DeleteGenieMailboxesInput {
  userId: string;
  onProgress?: (processedGenies: number) => void;
}

export const useDeleteGenieMailboxes = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // The edge function deletes one slice of the user's genies per call, so
    // an account with hundreds of genies never runs into its time limit;
    // this keeps calling until it reports nothing is left.
    mutationFn: async ({ userId, onProgress }: DeleteGenieMailboxesInput) => {
      const results: GenieMailboxDeletionResult[] = [];
      let afterAgentId: string | null = null;

      do {
        const token = await TokenService.getAccessToken();
        if (!token) {
          throw new Error("Session expired. Please refresh the page and try again.");
        }

        const { data, error } = await supabase.functions.invoke("handle-agent", {
          body: {
            action: "async-mailbox-delete-bulk",
            userId,
            ...(afterAgentId ? { afterAgentId } : {}),
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (error || !data?.success) {
          const reason = error?.message || data?.error || "Failed to delete genie mailboxes";
          throw new Error(
            results.length > 0
              ? `Stopped after ${results.length} genies: ${reason}. Run it again to continue.`
              : reason,
          );
        }

        results.push(...(data.results as GenieMailboxDeletionResult[]));
        afterAgentId = data.nextAfterAgentId as string | null;
        onProgress?.(results.length);
      } while (afterAgentId);

      return results;
    },
    onSuccess: (results) => {
      const deleted = results.filter((r) => r.status === "deleted").length;
      const failed = results.filter((r) => r.status === "failed");

      if (deleted > 0) {
        toast.success(`${deleted} mailbox${deleted === 1 ? "" : "es"} deleted`);
      } else if (failed.length === 0) {
        toast.info("This user has no genie mailboxes to delete");
      }

      if (failed.length > 0) {
        toast.error(
          `Failed to delete ${failed.length} mailbox${failed.length === 1 ? "" : "es"}: ${failed.map((f) => f.agentName).join(", ")}`,
        );
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete genie mailboxes");
    },
    onSettled: (_data, _error, { userId }) => {
      queryClient.invalidateQueries({ queryKey: genieMailboxProvisioningStatusQueryKey(userId) });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      await ApiService.invoke({
        resource: "profiles",
        action: "delete",
        id: userId,
      });
    },
    onSuccess: () => {
      toast.success("User deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["users-with-subscriptions"] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete user: ${error.message}`);
    },
  });
};

export const useResetPassword = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (payload: ResetPasswordPayload) => {
      if (!payload.password.trim()) {
        throw new Error("Password cannot be empty");
      }
      if (payload.password.length < 6) {
        throw new Error("Password must be at least 6 characters");
      }
      await UserService.updateUserPassword(payload.userId, payload.password);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users-with-subscriptions"] });
    },
    onError: (error: Error) => {
      console.error("Error resetting password:", error);
    },
  });

  return {
    resetPassword: (payload: ResetPasswordPayload) => mutation.mutateAsync(payload),
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateUserPayload) => {
      const { userId, profileUpdates, password } = payload;

      if (password?.trim()) {
        if (password.length < 6) {
          throw new Error("Password must be at least 6 characters");
        }
        await UserService.updateUserPassword(userId, password);
      }

      if (profileUpdates && Object.keys(profileUpdates).length > 0) {
        await UserService.updateUserProfile(userId, profileUpdates);
      }
    },
    onSuccess: (_, payload) => {
      const messages: string[] = [];
      if (payload.password) messages.push("Password updated");
      if (payload.profileUpdates && Object.keys(payload.profileUpdates).length > 0) messages.push("Profile updated");

      const message = messages.length > 0 ? messages.join(" • ") : "User updated";
      toast.success(message);

      queryClient.invalidateQueries({ queryKey: ["users-with-subscriptions"] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to update user: ${error.message}`);
    },
  });
};

export const useCreateSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateSubscriptionPayload) => {
      const { ApiService } = await import("@/services/api/ApiService");

      await ApiService.invoke({
        resource: "billing",
        action: "create",
        data: {
          userId: payload.userId,
          status: payload.status,
          quantity: payload.quantity,
          extra_quantity: payload.extra_quantity,
          stripe_id: payload.stripe_id,
          trial_ends_at: payload.trial_ends_at,
          ends_at: payload.ends_at,
        },
      });
    },
    onSuccess: () => {
      toast.success("Subscription created successfully");
      queryClient.invalidateQueries({ queryKey: ["users-with-subscriptions"] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to create subscription: ${error.message}`);
    },
  });
};

export const useUpdateSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateSubscriptionPayload) => {
      const { ApiService } = await import("@/services/api/ApiService");

      await ApiService.invoke({
        resource: "billing",
        action: "update",
        data: {
          userId: payload.userId,
          subscriptionId: payload.subscriptionId,
          status: payload.status,
          quantity: payload.quantity,
          extra_quantity: payload.extra_quantity,
          stripe_id: payload.stripe_id,
          trial_ends_at: payload.trial_ends_at,
          ends_at: payload.ends_at,
        },
      });
    },
    onSuccess: () => {
      toast.success("Subscription updated successfully");
      queryClient.invalidateQueries({ queryKey: ["users-with-subscriptions"] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to update subscription: ${error.message}`);
    },
  });
};

export const useDeleteSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { userId: string; subscriptionId: string | number }) => {
      const { ApiService } = await import("@/services/api/ApiService");

      await ApiService.invoke({
        resource: "billing",
        action: "delete",
        id: String(payload.subscriptionId),
      });
    },
    onSuccess: () => {
      toast.success("Subscription deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["users-with-subscriptions"] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete subscription: ${error.message}`);
    },
  });
};

export const UNLINKED_STRIPE_SUBSCRIPTIONS_QUERY_KEY = ["unlinked-stripe-subscriptions"];

export const useLinkStripeCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { userId: string; customerId: string }) => {
      const { SubscriptionService } = await import("@/services/billing/SubscriptionService");
      return SubscriptionService.linkStripeCustomer(payload.userId, payload.customerId);
    },
    onSuccess: (result) => {
      toast.success(result.message);
      if (result.skipped.length > 0) {
        toast.warning(`Not synced: ${result.skipped.join("; ")}`);
      }
      for (const adjustment of result.adjustments) {
        toast.info("Admin subscription moved onto Stripe", {
          description: adjustment,
          duration: Infinity,
          closeButton: true,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["users-with-subscriptions"] });
      queryClient.invalidateQueries({ queryKey: UNLINKED_STRIPE_SUBSCRIPTIONS_QUERY_KEY });
    },
    onError: (error: Error) => {
      toast.error(`Failed to link Stripe customer: ${error.message}`);
    },
  });
};

export const useUnlinkedStripeSubscriptions = (enabled: boolean) => {
  return useQuery({
    queryKey: UNLINKED_STRIPE_SUBSCRIPTIONS_QUERY_KEY,
    queryFn: async () => {
      const { SubscriptionService } = await import("@/services/billing/SubscriptionService");
      return SubscriptionService.listUnlinkedSubscriptions();
    },
    enabled,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useStripeCustomerInspection = (customerId: string | null, mode: "live" | "test") => {
  return useQuery({
    queryKey: ["stripe-customer-inspection", customerId, mode],
    queryFn: async () => {
      const { SubscriptionService } = await import("@/services/billing/SubscriptionService");
      return SubscriptionService.inspectStripeCustomer(customerId as string, mode);
    },
    enabled: !!customerId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
};
